import { useState, useEffect } from 'react'
import { useAssessmentStore } from '../../store/assessmentStore'
import { useAssessmentDefinitionStore } from '../../store/assessmentDefinitionStore'
import { useAvatar } from '../Avatar'
import { useSpeechQueueStore } from '../../store/speechQueueStore'
import { checkSystemHealth, getHealthSummary, isSystemReady, type SystemHealth } from '../../services/healthCheck'

// Face API URL for session creation
const FACE_API_URL = 'https://reallyassess-face-api-dev.techtree.workers.dev'

interface SessionResponse {
  success: boolean
  session: {
    id: string
    learnerId: string
    learnerName: string
    state: string
  }
  token: string
}

export default function LoginStep() {
  const { setLearner, setStep, setSessionId, setSessionToken, avatarState } = useAssessmentStore()
  const assessmentSlug = useAssessmentDefinitionStore((s) => s.slug)
  const assessmentTitle = useAssessmentDefinitionStore((s) => s.definition?.title)
  const assessmentSubtitle = useAssessmentDefinitionStore((s) => s.definition?.subtitle)
  const { speak, speakAndWait, unlockAudio } = useAvatar()
  const { isSpeaking } = useSpeechQueueStore()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({})
  const [started, setStarted] = useState(false)
  const [healthCheck, setHealthCheck] = useState<SystemHealth | undefined>(undefined)
  const [isCheckingHealth, setIsCheckingHealth] = useState(false)
  const [isCreatingSession, setIsCreatingSession] = useState(false)

  // Run health check when avatar is ready
  useEffect(() => {
    if (avatarState.isReady && !healthCheck && !isCheckingHealth) {
      setIsCheckingHealth(true)
      checkSystemHealth()
        .then(health => {
          setHealthCheck(health)
          setIsCheckingHealth(false)
        })
        .catch(() => {
          // If health check fails, assume unhealthy
          setHealthCheck({
            overall: 'unhealthy',
            services: [],
            timestamp: Date.now(),
          })
          setIsCheckingHealth(false)
        })
    }
  }, [avatarState.isReady, healthCheck, isCheckingHealth])

  // Handle "Get Started" click - unlocks audio and greets
  const handleGetStarted = async () => {
    // If no health check yet, run it now
    if (!healthCheck) {
      setIsCheckingHealth(true)
      const health = await checkSystemHealth()
      setHealthCheck(health)
      setIsCheckingHealth(false)

      if (!isSystemReady(health)) {
        speak("I'm sorry, some required services are currently unavailable. Please check your internet connection and try again.")
        return
      }
    }

    unlockAudio()
    setStarted(true)
    setTimeout(() => {
      speak("Hello! I'm Stella, your virtual assessor. Please enter your name and email to begin. This information will appear on your certificate.")
    }, 300)
  }

  const validate = () => {
    const newErrors: { name?: string; email?: string } = {}

    if (!name.trim()) {
      newErrors.name = 'Name is required for your certificate'
    } else if (name.trim().length < 2) {
      newErrors.name = 'Please enter your full name'
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      speak("Please fill in all required fields correctly.")
      return
    }

    setIsCreatingSession(true)

    try {
      // Create server-side session
      const response = await fetch(`${FACE_API_URL}/api/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          learnerName: name.trim(),
          learnerEmail: email.trim(),
          metadata: assessmentSlug
            ? { assessmentSlug, assessmentId: assessmentSlug }
            : undefined,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        console.error('[LoginStep] Session creation failed:', errorData)
        speak("I'm having trouble setting up your session. Please try again.")
        setIsCreatingSession(false)
        return
      }

      const sessionData = await response.json() as SessionResponse

      // Store session info
      setSessionId(sessionData.session.id)
      setSessionToken(sessionData.token)

      // Create learner with session-provided ID
      setLearner({
        id: sessionData.session.id,
        name: name.trim(),
        email: email.trim(),
      })

      // Avatar confirms and waits for speech to complete before moving to next step
      await speakAndWait(`Thank you, ${name.split(' ')[0]}. Now let's set up face recognition for secure proctoring.`)
      setStep('face-enrollment')
    } catch (error) {
      console.error('[LoginStep] Session creation error:', error)
      speak("Something went wrong. Please check your connection and try again.")
    } finally {
      setIsCreatingSession(false)
    }
  }

  return (
    <div className="flex-1 flex flex-col relative">
      {/* Get Started Overlay - shows until user clicks */}
      {!started && avatarState.isReady && (
        <div className="absolute inset-0 bg-assess-bg/95 z-30 flex items-center justify-center rounded-2xl">
          <div className="text-center max-w-md p-8">
            <div className="w-20 h-20 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">{assessmentTitle ?? 'ReallyAssess'}</h1>
            {assessmentSubtitle?.trim() && (
              <p className="text-gray-400 mb-8">{assessmentSubtitle}</p>
            )}
            {!assessmentSubtitle?.trim() && <div className="mb-8" />}
            <button
              onClick={handleGetStarted}
              disabled={isCheckingHealth || (healthCheck && !isSystemReady(healthCheck))}
              className={`
                font-semibold py-4 px-8 rounded-xl transition-colors text-lg
                flex items-center justify-center gap-3 mx-auto
                ${isCheckingHealth || (healthCheck && !isSystemReady(healthCheck))
                  ? 'bg-gray-600 cursor-not-allowed opacity-50'
                  : 'bg-primary-500 hover:bg-primary-600 text-white'
                }
              `}
            >
              {isCheckingHealth ? (
                <>
                  <svg className="w-6 h-6 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Checking System...
                </>
              ) : healthCheck && !isSystemReady(healthCheck) ? (
                <>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  System Unavailable
                </>
              ) : (
                <>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Get Started
                </>
              )}
            </button>

            {/* Health status indicator */}
            {healthCheck && (
              <div className={`text-xs mt-4 ${
                healthCheck.overall === 'healthy' ? 'text-green-400' :
                healthCheck.overall === 'degraded' ? 'text-yellow-400' :
                'text-red-400'
              }`}>
                {healthCheck.overall === 'healthy' && (
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    {getHealthSummary(healthCheck)}
                  </div>
                )}
                {healthCheck.overall === 'degraded' && (
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {getHealthSummary(healthCheck)}
                  </div>
                )}
                {healthCheck.overall === 'unhealthy' && (
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    {getHealthSummary(healthCheck)}
                  </div>
                )}
              </div>
            )}

            {!healthCheck && !isCheckingHealth && (
              <p className="text-xs text-gray-500 mt-4">
                Click to enable audio and meet Stella
              </p>
            )}
          </div>
        </div>
      )}

      {/* Form Content */}
      <div className="glass-card p-6 lg:p-8 flex-1">
        <div className="text-center mb-6 lg:mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">Welcome</h2>
          {assessmentSubtitle?.trim() && (
            <p className="text-gray-400">{assessmentSubtitle}</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name field */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name"
              className={`
                w-full px-4 py-3 bg-assess-bg border rounded-lg text-white
                placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500
                ${errors.name ? 'border-red-500' : 'border-assess-border'}
              `}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-400">{errors.name}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              This name will appear on your certificate
            </p>
          </div>

          {/* Email field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className={`
                w-full px-4 py-3 bg-assess-bg border rounded-lg text-white
                placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500
                ${errors.email ? 'border-red-500' : 'border-assess-border'}
              `}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-400">{errors.email}</p>
            )}
          </div>

          {/* Info box */}
          <div className="bg-primary-500/10 border border-primary-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-primary-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm">
                <p className="text-primary-300 font-medium">What to expect:</p>
                <ul className="text-gray-400 mt-1 space-y-1">
                  <li>• Face enrollment for identity verification</li>
                  <li>• Proctored ReallyAvatar assessment</li>
                  <li>• Continuous integrity monitoring</li>
                  <li>• Integrity score certificate upon completion</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={isCreatingSession || isSpeaking}
            className={`w-full font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 ${
              isCreatingSession || isSpeaking
                ? 'bg-gray-600 cursor-not-allowed'
                : 'bg-primary-500 hover:bg-primary-600 text-white'
            }`}
          >
            {isCreatingSession ? (
              <>
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Creating Session...
              </>
            ) : isSpeaking ? (
              <>
                <svg className="w-5 h-5 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
                Wait for Stella...
              </>
            ) : (
              <>
                Continue
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  )
}
